import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { trpc } from "@/trpc";
import {
  useMemberEventDetail,
  useMemberEventRsvp,
  useMemberRsvpCancel,
} from "@/queries/member-events";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@satyrsmc/shared/components";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AttendeeStatus, EventType, PaymentMethod, PaymentStatus } from "@satyrsmc/shared/client";
import { resolveEventCost } from "@satyrsmc/shared/lib/event-pricing";
import { isDayPassAvailable } from "@satyrsmc/shared/lib/event-days";
import { includesTshirt } from "@satyrsmc/shared/lib/day-pass-policy";
import { getEventRegistrationBlock, isPastRefundCutoff } from "@/lib/eventUtils";
import { useAuth } from "@/state/AuthContext";
import { WizardProvider, useWizard } from "@/components/events/wizard/WizardContext";
import { WizardShell } from "@/components/events/wizard/WizardShell";
import { Step1Details } from "@/components/events/wizard/Step1Details";
import { Step2Waiver } from "@/components/events/wizard/Step2Waiver";
import { Step3Payment } from "@/components/events/wizard/Step3Payment";
import { Step4Confirmation } from "@/components/events/wizard/Step4Confirmation";
import type { MemberEventDetail } from "@satyrsmc/shared/dto/member/event";

// ─── Cancel dialog ────────────────────────────────────────────────────────────

function CancelRsvpDialog({
  eventId,
  rsvp,
  event,
}: {
  eventId: string;
  rsvp: { id: string; paymentStatus: string; paymentMethod: string | null };
  event: { refund_cutoff_date: string | null };
}) {
  const navigate = useNavigate();
  const cancelMutation = useMemberRsvpCancel();
  const [open, setOpen] = useState(true);

  const isCreditCardConfirmed =
    rsvp.paymentMethod === PaymentMethod.CreditCard &&
    rsvp.paymentStatus === PaymentStatus.Confirmed;
  const isOtherPaymentConfirmed =
    rsvp.paymentMethod !== null &&
    rsvp.paymentMethod !== PaymentMethod.CreditCard &&
    rsvp.paymentStatus === PaymentStatus.Confirmed;
  const cutoffPassed = isPastRefundCutoff(event.refund_cutoff_date);

  const description = cutoffPassed
    ? "This event's RSVP cutoff date has passed — RSVPs can no longer be cancelled. Please contact an admin for help."
    : isCreditCardConfirmed
      ? "A refund will be issued to your credit card."
      : isOtherPaymentConfirmed
        ? "A refund request will be submitted to the treasurer."
        : "Your RSVP will be cancelled.";

  const handleClose = () => {
    setOpen(false);
    navigate(`/events/${eventId}`);
  };

  const handleConfirm = () => {
    cancelMutation.mutate(
      { rsvpId: rsvp.id },
      {
        onSuccess: () => {
          toast.success("RSVP cancelled.");
          navigate(`/events/${eventId}`);
        },
        onError: (err) => {
          toast.error(err.message || "Something went wrong.");
          setOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{cutoffPassed ? "RSVPs are locked" : "Cancel your RSVP?"}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {cutoffPassed ? (
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={cancelMutation.isPending}>
                Keep my RSVP
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirm}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? "Cancelling…" : "Yes, cancel it"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Wizard orchestrator ──────────────────────────────────────────────────────

function RsvpWizard({ event, onStart }: { event: MemberEventDetail; onStart: () => void }) {
  const { state, dispatch, stripeConfirmRef } = useWizard();
  const rsvpMutation = useMemberEventRsvp();
  const [submitted, setSubmitted] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  useEffect(() => {
    onStart();
  }, [onStart]);

  // Gate the Payment step on what THIS viewer is actually charged, mirroring the
  // server's charge logic (resolveEventCost). A campout that's free for members
  // (member_cost = 0) must not show a Payment step even if it has a GA ticket price.
  const { isMember } = useAuth();
  const isDayPass = state.attendance.kind === "single";
  const effectiveCost = resolveEventCost(event, isMember, isDayPass);
  const isPaidEvent = effectiveCost != null && effectiveCost > 0;
  const isCampout =
    event.event_type === EventType.Badger || event.event_type === EventType.PioneerRun;

  const steps = [
    { label: "Details" },
    { label: "Waiver" },
    ...(isPaidEvent ? [{ label: "Payment" }] : []),
    { label: "Confirm" },
  ];

  const confirmStepIndex = steps.length - 1;
  const paymentStepIndex = isPaidEvent ? 2 : -1;

  // ── Step validity ──────────────────────────────────────────────────────────

  const step1Valid = (() => {
    // When day passes are offered the registrant must explicitly choose full
    // event vs single day — no default. When they're not, there's no choice to
    // make and the unset state means "the whole event".
    if (isDayPassAvailable(event) && state.attendance.kind === "unset") return false;
    // "A single day" with no day picked is an unfinished answer, not a full-event
    // registration — it must not be able to advance and submit as one.
    if (state.attendance.kind === "single" && state.attendance.date === null) return false;
    if (event.event_type === EventType.Rides) {
      return state.isRider !== null;
    }
    if (isCampout) {
      const details = state.campoutDetails;
      if (!details) return false;
      if (!details.travelingBy) return false;
      // The t-shirt requirement follows the t-shirt field: a day pass has neither,
      // so requiring a size here would strand the registrant on a hidden question.
      if (event.event_type === EventType.Badger && includesTshirt(isDayPass) && !details.tshirtSize)
        return false;
      return true;
    }
    // Social — dietary restrictions are optional, step is always valid
    return true;
  })();

  const step2Valid = state.waiverSigned;

  const step3Valid = (() => {
    if (!isPaidEvent) return true;
    if (!state.paymentMethod) return false;
    if (state.paymentMethod === PaymentMethod.CreditCard) {
      return !!state.stripeClientSecret;
    }
    return true;
  })();

  const canAdvance = (() => {
    switch (state.step) {
      case 0:
        return step1Valid;
      case 1:
        return step2Valid;
      case 2:
        return paymentStepIndex === 2 ? step3Valid : true;
      default:
        return false;
    }
  })();

  // ── Submit mutation ────────────────────────────────────────────────────────

  const submitRsvp = (externalPaymentId?: string) => {
    const details = state.campoutDetails;
    rsvpMutation.mutate(
      {
        eventId: event.id,
        status: AttendeeStatus.Yes,
        waiver_signed: true,
        paymentMethod: isPaidEvent && state.paymentMethod ? state.paymentMethod : undefined,
        externalPaymentId,
        campoutDetails:
          isCampout && details
            ? {
                tshirtSize: details.tshirtSize ?? undefined,
                travelingBy: details.travelingBy ?? undefined,
                club: details.club.trim() || null,
              }
            : undefined,
        bikeId: state.bikeId,
        isRider: state.isRider,
        dietaryRestrictions: state.dietaryRestrictions,
        attendanceDate: state.attendance.kind === "single" ? state.attendance.date : null,
      },
      {
        onSuccess: () => {
          setSubmitted(true);
          setRetryError(null);
          dispatch({ type: "SET_STEP", step: confirmStepIndex });
        },
        onError: (err) => {
          setRetryError(err.message || "Something went wrong. Please try again.");
          dispatch({ type: "SET_STEP", step: confirmStepIndex });
        },
      },
    );
  };

  // ── Navigation ─────────────────────────────────────────────────────────────

  const handleNext = async () => {
    const nextStep = state.step + 1;

    // Advancing from payment step with CC — confirm payment first
    if (state.step === paymentStepIndex && state.paymentMethod === PaymentMethod.CreditCard) {
      if (!stripeConfirmRef.current) {
        toast.error("Payment form is not ready. Please wait or try selecting Credit Card again.");
        return;
      }
      try {
        const paymentIntentId = await stripeConfirmRef.current();
        dispatch({ type: "SET_EXTERNAL_PAYMENT_ID", id: paymentIntentId });
        submitRsvp(paymentIntentId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Payment failed. Please try again.");
      }
      return;
    }

    // Advancing to confirmation step — submit
    if (nextStep === confirmStepIndex) {
      submitRsvp();
      return;
    }

    dispatch({ type: "SET_STEP", step: nextStep });
  };

  const handleBack = () => {
    dispatch({ type: "SET_STEP", step: state.step - 1 });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const renderStep = () => {
    const confirmationStep = (
      <Step4Confirmation
        event={event}
        onRetry={retryError ? () => submitRsvp(state.externalPaymentId ?? undefined) : undefined}
        retryError={retryError}
        submitting={rsvpMutation.isPending}
      />
    );
    switch (state.step) {
      case 0:
        return <Step1Details event={event} />;
      case 1:
        return <Step2Waiver />;
      case 2:
        return paymentStepIndex === 2 ? <Step3Payment event={event} /> : confirmationStep;
      case 3:
        return confirmationStep;
      default:
        return null;
    }
  };

  return (
    <WizardShell
      eventId={event.id}
      steps={steps}
      canAdvance={canAdvance && !rsvpMutation.isPending}
      onNext={handleNext}
      onBack={handleBack}
      submitted={submitted}
    >
      {renderStep()}
    </WizardShell>
  );
}

// ─── Manager ──────────────────────────────────────────────────────────────────

function RsvpManager({ eventId }: { eventId: string }) {
  const { data: event, isLoading: eventLoading } = useMemberEventDetail(eventId);
  const { data: rsvp, isLoading: rsvpLoading } = trpc.members.rsvps.getForEvent.useQuery({
    eventId,
  });
  // Once the wizard mounts, keep it mounted until the user navigates away — prevents
  // the post-submit rsvp refetch (which returns Yes) from swapping us into the cancel dialog.
  const [wizardStarted, setWizardStarted] = useState(false);

  if (eventLoading || rsvpLoading) {
    return <p className="text-muted-foreground">Loading…</p>;
  }

  if (!event) {
    return <p className="text-muted-foreground">Event not found.</p>;
  }

  const block = getEventRegistrationBlock({
    status: event.status,
    start_date: event.start_date,
    end_date: event.end_date,
  });
  if (block.blocked) {
    return (
      <div
        className={`rounded-lg border p-6 text-center ${block.variant === "error" ? "border-destructive/20 bg-destructive/10" : "border-border bg-muted/40"}`}
      >
        <p
          className={`font-medium ${block.variant === "error" ? "text-destructive" : "text-muted-foreground"}`}
        >
          {block.reason}
        </p>
      </div>
    );
  }

  if (rsvp?.status === AttendeeStatus.Yes && !wizardStarted) {
    return <CancelRsvpDialog eventId={eventId} rsvp={rsvp} event={event} />;
  }

  return (
    <WizardProvider>
      <RsvpWizard event={event} onStart={() => setWizardStarted(true)} />
    </WizardProvider>
  );
}

// ─── Page entry point ─────────────────────────────────────────────────────────

export function EventRegisterPage() {
  const { id: eventId } = useParams<{ id: string }>();

  if (!eventId) return <Navigate to="/events" replace />;

  return (
    <PageContainer>
      <RsvpManager eventId={eventId} />
    </PageContainer>
  );
}
